import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { motion, AnimatePresence } from 'framer-motion';
import { useShop } from '../../context/ShopContext';

const { FiShoppingBag, FiSearch, FiMenu, FiX, FiUser } = FiIcons;

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { cart, setIsCartOpen, setIsRegistryOpen } = useShop();

  const cartCount = cart.reduce((sum, item) => sum + item.quantity, 0);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <>
      <div className="bg-luma-primary text-white py-2 px-4 text-center text-[10px] tracking-[0.2em] uppercase font-medium z-[60] relative">
        Complimentary Shipping on all Registry Orders over $150
      </div>

      <header className={`fixed w-full top-0 left-0 z-50 transition-all duration-500 ${isScrolled ? 'bg-luma-bg/90 backdrop-blur-md translate-y-0 shadow-sm' : 'bg-transparent pt-8'}`}>
        <div className="max-w-7xl mx-auto px-6 lg:px-10 flex items-center justify-between h-16 md:h-20">
          
          <button 
            aria-label="Toggle Menu"
            className="lg:hidden p-2 -ml-2" 
            onClick={() => setMobileMenuOpen(true)}
          >
            <SafeIcon icon={FiMenu} className="text-2xl" />
          </button>

          <nav className="hidden lg:flex items-center gap-10 text-[11px] tracking-[0.15em] uppercase font-semibold text-luma-text/80">
            <Link to="/" className="hover:text-luma-primary transition-colors py-2">Shop</Link>
            <button 
              onClick={() => setIsRegistryOpen(true)}
              className="hover:text-luma-primary transition-colors py-2 uppercase tracking-[0.15em]"
            >
              Registry
            </button>
            <Link to="/" className="hover:text-luma-primary transition-colors py-2">Our Story</Link>
          </nav>

          <Link to="/" className="absolute left-1/2 -translate-x-1/2 flex flex-col items-center group">
            <span className="font-serif text-2xl md:text-3xl tracking-tight text-luma-text leading-none transition-all group-hover:tracking-widest">LumaBaby</span>
            <span className="text-[8px] tracking-[0.4em] uppercase mt-1 opacity-60">Est. 2024</span>
          </Link>

          <div className="flex items-center gap-4 md:gap-6">
            <button aria-label="Search" className="hidden sm:block hover:text-luma-primary transition-colors p-2 focus:outline-none focus:ring-2 focus:ring-luma-primary/20 rounded-full">
              <SafeIcon icon={FiSearch} className="text-xl" />
            </button>
            <button aria-label="Account" className="hidden sm:block hover:text-luma-primary transition-colors p-2 focus:outline-none focus:ring-2 focus:ring-luma-primary/20 rounded-full">
              <SafeIcon icon={FiUser} className="text-xl" />
            </button>
            <button 
              aria-label={`Shopping cart with ${cartCount} items`}
              onClick={() => setIsCartOpen(true)}
              className="relative hover:text-luma-primary transition-colors p-2 focus:outline-none focus:ring-2 focus:ring-luma-primary/20 rounded-full"
            >
              <SafeIcon icon={FiShoppingBag} className="text-xl" />
              {cartCount > 0 && (
                <motion.span 
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  className="absolute top-1 right-0 bg-luma-primary text-white text-[9px] w-4 h-4 rounded-full flex items-center justify-center font-bold"
                >
                  {cartCount}
                </motion.span>
              )}
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div 
            initial={{ x: '-100%' }}
            animate={{ x: 0 }}
            exit={{ x: '-100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
            className="fixed inset-0 bg-luma-bg z-[70] p-8 flex flex-col"
          >
            <div className="flex justify-between items-center mb-16">
              <span className="font-serif text-2xl">LumaBaby</span>
              <button onClick={() => setMobileMenuOpen(false)}>
                <SafeIcon icon={FiX} className="text-3xl" />
              </button>
            </div>
            <nav className="flex flex-col gap-8">
              {['New Arrivals', 'Shop by Category', 'Registry Services', 'Gift Guides', 'About LumaBaby'].map((item) => (
                <Link key={item} to="/" className="font-serif text-3xl text-luma-text/90" onClick={() => setMobileMenuOpen(false)}>
                  {item}
                </Link>
              ))}
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Header;